import {
  toKvRows,
  mapInsomniaAuth,
  mapInsomniaBody,
  normalizeMethod,
  variablesToCollectionSettings,
} from './normalize';
import { defaultCollectionAuth } from '../requestAuth';

function buildUrl(url) {
  if (!url) return '';
  if (typeof url === 'string') return url;
  return url.raw || url.href || '';
}

/**
 * @param {object} root Insomnia v4 export
 * @param {string[]} warnings
 */
export function parseInsomniaExport(root, warnings) {
  const resources = root.resources || [];
  const byId = new Map();
  for (const r of resources) {
    if (r?._id) byId.set(r._id, r);
  }

  const workspace = resources.find((r) => r._type === 'Workspace');
  const defaultName = workspace?.name || root.name || 'Imported from Insomnia';

  const groups = resources.filter((r) => r._type === 'RequestGroup');
  const requests = resources.filter((r) => r._type === 'Request');

  const collections = [];

  const groupIds = new Set(groups.map((g) => g._id));

  function parentGroupId(request) {
    let pid = request.parentId;
    while (pid) {
      const parent = byId.get(pid);
      if (!parent) break;
      if (parent._type === 'RequestGroup') return parent._id;
      pid = parent.parentId;
    }
    return null;
  }

  function mapRequest(req) {
    const url = buildUrl(req.url);
    const headerRows = toKvRows(
      (req.headers || []).map((h) => ({
        key: h.name ?? h.key,
        value: h.value,
        disabled: h.disabled === true || h.isDisabled === true,
      }))
    );
    const params = toKvRows(
      (req.parameters || []).filter((p) => p.type === 'query' || !p.type).map((p) => ({
        key: p.name ?? p.key,
        value: p.value,
        disabled: p.disabled === true,
      }))
    );
    const { body, body_type } = mapInsomniaBody(req.body, warnings);
    const mapped = {
      name: req.name || 'Request',
      description: req.description || '',
      method: normalizeMethod(req.method),
      url,
      headers: headerRows,
      params,
      cookies: [],
      body,
      body_type,
      auth: mapInsomniaAuth(req.authentication, warnings),
      sort_order: 0,
    };
    // Insomnia ≥2023.5 stores scripts inline on the request (pm-compatible API).
    if (req.preRequestScript) mapped.preRequestScript = req.preRequestScript;
    if (req.afterResponseScript) mapped.testScript = req.afterResponseScript;
    if (mapped.preRequestScript || mapped.testScript) mapped.scriptDialect = 'pm';
    return mapped;
  }

  for (const group of groups) {
    const groupRequests = requests
      .filter((r) => parentGroupId(r) === group._id)
      .map((r, idx) => ({ ...mapRequest(r), sort_order: idx }));

    const settings = variablesToCollectionSettings(group.environment || group.meta);

    collections.push({
      name: group.name || 'Collection',
      description: group.description || '',
      settings: {
        ...settings,
        collection_auth: defaultCollectionAuth(),
      },
      requests: groupRequests,
    });
  }

  const ungrouped = requests
    .filter((r) => {
      const gid = parentGroupId(r);
      return !gid || !groupIds.has(gid);
    })
    .map((r, idx) => ({ ...mapRequest(r), sort_order: idx }));

  if (ungrouped.length > 0) {
    collections.push({
      name: groups.length > 0 ? `${defaultName} (Other)` : defaultName,
      description: '',
      settings: { collection_auth: defaultCollectionAuth() },
      requests: ungrouped,
    });
  }

  if (collections.length === 0 && requests.length > 0) {
    collections.push({
      name: defaultName,
      description: '',
      settings: { collection_auth: defaultCollectionAuth() },
      requests: requests.map((r, idx) => ({ ...mapRequest(r), sort_order: idx })),
    });
  }

  return collections;
}
